/**
 * Icon Mapping Utility
 *
 * Converts MDI icon names to emoji equivalents for kid-friendly display.
 * Used across kid dashboard components for consistent icon rendering.
 */

/**
 * Map of MDI icon names to emoji equivalents
 */
const iconMap: Record<string, string> = {
  // Household chores
  'mdi:broom': '🧹',
  'mdi:vacuum': '🧹',
  'mdi:trash-can': '🗑️',
  'mdi:recycle': '♻️',
  'mdi:washing-machine': '🧺',
  'mdi:tumble-dryer': '🧺',
  'mdi:iron': '👔',
  'mdi:spray-bottle': '🧴',

  // Kitchen & meals
  'mdi:silverware-fork-knife': '🍽️',
  'mdi:food': '🍕',
  'mdi:food-apple': '🍎',
  'mdi:cup': '☕',
  'mdi:pot-steam': '🍲',
  'mdi:fridge': '🧊',

  // Bedroom & bathroom
  'mdi:bed': '🛏️',
  'mdi:shower': '🚿',
  'mdi:tooth': '🦷',
  'mdi:towel': '🧻',

  // Pets
  'mdi:dog': '🐕',
  'mdi:cat': '🐈',
  'mdi:fish': '🐟',
  'mdi:paw': '🐾',

  // Outdoor
  'mdi:water': '💧',
  'mdi:flower': '🌸',
  'mdi:grass': '🌿',
  'mdi:tree': '🌳',
  'mdi:car': '🚗',
  'mdi:bike': '🚲',

  // School & learning
  'mdi:school': '🎒',
  'mdi:book': '📚',
  'mdi:pencil': '✏️',
  'mdi:calculator': '🔢',
  'mdi:music': '🎵',
  'mdi:piano': '🎹',

  // General
  'mdi:home': '🏠',
  'mdi:account': '👤',
  'mdi:check-circle': '✅',
  'mdi:star': '⭐',
  'mdi:heart': '❤️',
  'mdi:clock': '⏰',
  'mdi:calendar': '📅',
  'mdi:gift': '🎁',
  'mdi:trophy': '🏆',
}

/**
 * Default icon when no mapping is found
 */
const DEFAULT_ICON = '📋'

/**
 * Convert an icon string to an emoji display character.
 *
 * @param icon - The icon string (can be emoji, MDI icon name, or null/undefined)
 * @returns An emoji character for display
 *
 * @example
 * getIconDisplay('mdi:broom') // Returns '🧹'
 * getIconDisplay('🎮') // Returns '🎮' (passes through emoji)
 * getIconDisplay(null) // Returns '📋' (default)
 */
export const getIconDisplay = (icon: string | null | undefined): string => {
  // Return default if no icon provided
  if (!icon) return DEFAULT_ICON

  // If it's already an emoji (short string, not MDI format), return as-is
  if (icon.length <= 2 && !icon.startsWith('mdi:')) {
    return icon
  }

  // Look up in our icon map, fallback to default
  return iconMap[icon] || DEFAULT_ICON
}

/**
 * Check if a string is likely an emoji
 */
export const isEmoji = (str: string): boolean => {
  return str.length <= 2 && !str.startsWith('mdi:')
}

/**
 * Get all available icon mappings (useful for icon pickers)
 */
export const getAllIconMappings = (): Record<string, string> => {
  return { ...iconMap }
}

export default getIconDisplay
